import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import apiClient from '../../../requests/api/apiClient';
import { PhenologicalStateWithId } from '../../../requests/api/apiTypes';
import { ReduxState } from '../../../types';
import { FetchError } from '../../../views/types';
import {
  AlertModalMode,
  AlertModalProps,
  CultivationCycle,
  Limit,
  MAX_FREQUENCY_OPTIONS,
  REVIEW_SCHEDULE_OPTIONS,
  TIME_OUT_RANGE_OPTIONS,
  User,
  useStyles,
} from './constants';
import HumidityTemperatureAlertLimit from './HumidityTemperatureAlertLimit';
import SoilMoistureAlertLimit from './SoilMoistureAlertLimit';

const AlertModal: React.FC<AlertModalProps> = ({
  interval,
  open,
  onClose,
  sectorId,
  measurements,
  chartName,
  mode = AlertModalMode.CREATE,
  alertData,
  onAlertUpdated,
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);
  const { selectedAccount } = useSelector((state: ReduxState) => state.userAccount);

  const [cultivationCycles, setCultivationCycles] = useState<CultivationCycle[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [customUsers, setCustomUsers] = useState<User[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    cycle: '',
    phenologicalState: '',
    chart: chartName,
    startDate: '',
    endDate: '',
    message: '',
    maxFrequency: '',
    timeOutRange: '',
    timeEachReview: '',
    recipients: [] as string[],
  });

  const [limits, setLimits] = useState<Limit[]>([{ name: '', value: 0, top: true }]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [showAddDest, setShowAddDest] = useState(false);
  const [newDest, setNewDest] = useState({ name: '', email: '', phone: '' });

  const [soilGeneralLines, setSoilGeneralLines] = useState<any[]>([]);
  const [soilCurrentLines, setSoilCurrentLines] = useState<any[]>([]);
  const [activeSoilAlerts, setActiveSoilAlerts] = useState<{ [label: string]: boolean }>({});
  const isGraficoSuelo = chartName === 'humedad-suelo';
  const isViewMode = mode === AlertModalMode.VIEW;
  const endDateInPast = !!formData.endDate && dayjs(formData.endDate).isBefore(dayjs(), 'day');
  const [phenologicalStates, setPhenologicalStates] = useState<PhenologicalStateWithId[]>([]);

  useEffect(() => {
    if (alertData) {
      setFormData({
        name: alertData.name,
        cycle: alertData.cycle || '',
        phenologicalState: alertData.phenologicalState || '',
        chart: alertData.chart,
        startDate: new Date(alertData.startDate).toISOString().split('T')[0],
        endDate: new Date(alertData.endDate).toISOString().split('T')[0],
        message: alertData.message,
        maxFrequency: alertData.maxFrequency.toString(),
        timeOutRange: alertData.timeOutRange.toString(),
        timeEachReview: alertData.timeEachReview.toString(),
        recipients: alertData.recipients,
      });
      setLimits(alertData.limits);
      setSelectedUsers(alertData.recipients);
    }
  }, [alertData]);

  useEffect(() => {
    if (alertData && isGraficoSuelo) {
      const newActiveSoilAlerts: { [inputName: string]: boolean } = {};
      soilCurrentLines.forEach((line) => {
        // Check for both the original label and the customLabel in existing alerts
        const found = alertData.limits.find((lim) => lim.name === line.label
          || lim.name === line.customLabel);
        newActiveSoilAlerts[line.label] = !!found;
      });
      setActiveSoilAlerts(newActiveSoilAlerts);
    }
  }, [alertData, isGraficoSuelo, soilCurrentLines]);

  useEffect(() => {
    const fetchCultivationCycles = async () => {
      try {
        const response = await apiClient.cultivationCycleSector
          .cultivationCycleSectorDetail(sectorId);
        if (response.data.cultivationCycles) {
          setCultivationCycles(response.data.cultivationCycles);
        }
      } catch (err) {
        const { error } = err as FetchError;
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    };

    const fetchUsers = async () => {
      try {
        const response = await apiClient.users.usersForAlerts({
          filterAccount: selectedAccount?.id,
          filterFarm: selectedFarm?.id,
        });
        if (response.data.users) {
          setUsers(response.data.users.map((user) => ({
            id: user.id || '',
            name: `${user.name} ${user.lastname}`,
            email: user.email,
            phoneNumber: user.phoneNumber ?? undefined,
          })));
        }
      } catch (err) {
        const { error } = err as FetchError;
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    };

    if (sectorId) {
      fetchCultivationCycles();
    }
    if (selectedAccount?.id && selectedFarm?.id) {
      fetchUsers();
    }
  }, [sectorId, selectedAccount?.id, selectedFarm?.id, enqueueSnackbar, open]);

  useEffect(() => {
    if (!alertData) {
      setSelectedUsers(users.map((u) => u.email));
      return;
    }
    const knownEmails = users.map((u) => u.email);
    setCustomUsers(alertData.recipients
      .filter((email) => !knownEmails.includes(email))
      .map((email) => ({ id: `custom-${email}`, name: email, email })));
    setSelectedUsers(alertData.recipients);
  }, [users, alertData]);

  useEffect(() => {
    if (interval && isGraficoSuelo && sectorId) {
      apiClient.soilMoistureConfig.soilMoistureConfig(
        sectorId,
      )
        .then((response) => {
          const generalLines = response.data.soilMoistureConfig?.generalLines || [];
          generalLines.forEach((line: any) => {
            line.name = line.label;
          });
          setSoilGeneralLines(generalLines);
          if (formData.phenologicalState) {
            const selectedState = phenologicalStates.find(
              (state) => state.name === formData.phenologicalState,
            );
            if (selectedState) {
              const limitsWithName = selectedState.lines?.map((line: any) => ({
                ...line,
                name: line.label,
              }));
              setSoilCurrentLines(limitsWithName || []);
            }
          } else {
            setSoilCurrentLines(generalLines);
          }
          const initial: { [label: string]: boolean } = {};
          generalLines.forEach((line) => {
            if (line.label) {
              initial[line.label] = true;
            }
          });
          setActiveSoilAlerts(initial);
        })
        .catch(() => {
          setSoilCurrentLines([]);
          setActiveSoilAlerts({});
        });
    }
  }, [isGraficoSuelo, sectorId, open, interval]);

  const handleTextChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isViewMode) return;
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<string>) => {
    if (isViewMode) return;
    const { value } = event.target;
    setFormData({ ...formData, [field]: value });
    if (field === 'cycle') {
      setSoilCurrentLines(soilGeneralLines);
      setFormData((prev) => ({
        ...prev,
        phenologicalState: '',
        startDate: '',
        endDate: '',
      }));
      setPhenologicalStates(cultivationCycles.find(
        (cycle: any) => cycle.id === value,
      )?.phenologicalStates || []);
    }

    if (field === 'phenologicalState' && value) {
      const selectedState = phenologicalStates.find(
        (state) => state.name === value,
      );
      if (selectedState) {
        const limitsWithName = selectedState.lines?.map((line: any) => ({
          ...line,
          name: line.label,
        }));
        setSoilCurrentLines(limitsWithName || []);
        setFormData((prev) => ({
          ...prev,
          [field]: value,
          startDate: new Date(selectedState.startDate).toISOString().split('T')[0],
          endDate: new Date(selectedState.endDate).toISOString().split('T')[0],
        }));
      }
    }
  };

  const addLimit = () => {
    if (isViewMode) return;
    setLimits([...limits, { name: '', value: 0, top: true }]);
  };

  const handleToggleUser = (email: string) => {
    if (isViewMode) return;
    setSelectedUsers((prev) => {
      if (prev.includes(email)) {
        return prev.filter((e) => e !== email);
      }
      return [...prev, email];
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name
        || !formData.startDate
        || !formData.endDate
        || !formData.message
        || !formData.maxFrequency
        || !formData.timeOutRange
        || !formData.timeEachReview) {
        enqueueSnackbar('Por favor complete todos los campos requeridos', { variant: 'error' });
        return;
      }
      if (endDateInPast) {
        enqueueSnackbar('La fecha de fin no puede ser anterior a hoy', { variant: 'error' });
        return;
      }
      if (!isGraficoSuelo
        && (limits.length === 0 || limits.some((limit) => !limit.name || limit.value === 0))) {
        enqueueSnackbar('Por favor configure al menos un límite válido', { variant: 'error' });
        return;
      }
      if (selectedUsers.length === 0) {
        enqueueSnackbar('Por favor seleccione al menos un destinatario', { variant: 'error' });
        return;
      }
      let limitsToSend: Limit[] = limits;
      if (isGraficoSuelo) {
        limitsToSend = soilCurrentLines
          .filter((line) => activeSoilAlerts[line.name])
          .map((line) => ({
            name: line.customLabel || line.name,
            value: line.value,
            top: line.name !== 'minValue',
          }));
        if (limitsToSend.length === 0) {
          enqueueSnackbar(
            'Por favor active al menos un punto crítico para la alerta',
            { variant: 'error' },
          );
          return;
        }
      }

      if (mode === AlertModalMode.UPDATE && alertData && alertData.id) {
        const patchPayload: Record<string, string | number | string[] | Limit[]> = {
          name: formData.name,
          message: formData.message,
          maxFrequency: Number(formData.maxFrequency),
          timeOutRange: Number(formData.timeOutRange),
          timeEachReview: formData.timeEachReview,
          recipients: selectedUsers,
          chart: formData.chart,
          startDate: formData.startDate,
          endDate: formData.endDate,
        };
        Object.keys(patchPayload).forEach(
          (key) => (patchPayload[key] === '' || patchPayload[key] === undefined) && delete patchPayload[key],
        );
        await apiClient.alert.alertUpdate(alertData.id, patchPayload);
        enqueueSnackbar('Alerta actualizada exitosamente', { variant: 'success' });
      } else {
        const alertPayload = {
          name: formData.name,
          cycle: formData.cycle || undefined,
          phenologicalState: formData.phenologicalState,
          chart: formData.chart,
          startDate: new Date(formData.startDate).toISOString(),
          endDate: new Date(formData.endDate).toISOString(),
          limits: limitsToSend,
          message: formData.message,
          maxFrequency: Number(formData.maxFrequency),
          timeOutRange: Number(formData.timeOutRange),
          timeEachReview: formData.timeEachReview,
          sectorId,
          recipients: selectedUsers,
        };
        await apiClient.alert.alertCreate(alertPayload);
        enqueueSnackbar('Alerta creada exitosamente', { variant: 'success' });
      }
      onAlertUpdated?.();
      onClose();
    } catch (err) {
      const { error } = err as FetchError;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const getModalTitle = () => {
    switch (mode) {
      case AlertModalMode.CREATE:
        return 'Crear Alerta';
      case AlertModalMode.VIEW:
        return 'Ver Alerta';
      case AlertModalMode.UPDATE:
        return 'Actualizar Alerta';
      default:
        return 'Configuración de Alertas';
    }
  };

  const isFieldEditable = (field: string) => {
    if (mode === AlertModalMode.CREATE) return true;
    if (mode === AlertModalMode.UPDATE) {
      return [
        'name',
        'value',
        'top',
        'switch-limits',
        'message',
        'maxFrequency',
        'timeOutRange',
        'timeEachReview',
        'recipients',
      ].includes(field);
    }
    return false;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          background: '#f5f7fa',
          borderBottom: 'none',
          paddingY: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              display: 'inline-block',
              position: 'relative',
              marginBottom: 0.5,
            }}
          >
            {getModalTitle()}
            <Box
              sx={{
                height: 3,
                width: '100%',
                background: '#4285f4',
                position: 'absolute',
                left: 0,
                bottom: -6,
                borderRadius: 2,
              }}
            />
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Box className={classes.formContainer}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre de la alerta"
                value={formData.name}
                onChange={handleTextChange('name')}
                required
                disabled={!isFieldEditable('name')}
              />
            </Grid>
            {isFieldEditable('cycle') && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Ciclo de cultivo</InputLabel>
                  <Select
                    value={formData.cycle}
                    onChange={handleSelectChange('cycle')}
                    label="Ciclo de cultivo"
                    disabled={!isFieldEditable('cycle')}
                  >
                    <MenuItem value={undefined}>Ninguno</MenuItem>
                    {cultivationCycles.map((cycle: any) => (
                      <MenuItem key={cycle.id} value={cycle.id}>
                        {cycle.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {!isFieldEditable('cycle') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ciclo de cultivo"
                  value={formData.cycle}
                  disabled={true}
                />
              </Grid>
            )}
            {isFieldEditable('phenologicalState') && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Estado fenológico</InputLabel>
                  <Select
                    value={formData.phenologicalState}
                    onChange={handleSelectChange('phenologicalState')}
                    label="Estado fenológico"
                    disabled={!isFieldEditable('phenologicalState')}
                  >
                    <MenuItem value="">Ninguno</MenuItem>
                    {phenologicalStates.map((state) => (
                      <MenuItem key={state.name} value={state.name}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {!isFieldEditable('phenologicalState') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Estado fenológico"
                  value={formData.phenologicalState}
                  disabled={true}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Gráfico</InputLabel>
                <Select
                  value={formData.chart}
                  disabled
                  label="Gráfico"
                >
                  <MenuItem value={chartName}>{chartName}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Fecha de inicio"
                type="date"
                value={formData.startDate}
                onChange={handleTextChange('startDate')}
                InputLabelProps={{ shrink: true }}
                required
                disabled={!isFieldEditable('startDate') || formData.phenologicalState !== ''}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Fecha de fin"
                type="date"
                value={formData.endDate}
                onChange={handleTextChange('endDate')}
                InputLabelProps={{ shrink: true }}
                required
                disabled={!isFieldEditable('endDate') || formData.phenologicalState !== ''}
              />
            </Grid>
            {endDateInPast && !isViewMode && (
              <Grid item xs={12}>
                <Alert severity="error">
                  La fecha de fin no puede ser anterior a hoy: la alerta quedaría
                  {' '}
                  <strong>inactiva</strong>
                  {' '}
                  de inmediato. Elige una fecha de fin igual o posterior a hoy.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle1">Límites</Typography>
              {isGraficoSuelo ? (
                <SoilMoistureAlertLimit
                  open={open}
                  sectorId={sectorId}
                  alertData={alertData}
                  limits={alertData?.limits || soilCurrentLines}
                  activeSoilAlerts={activeSoilAlerts}
                  setActiveSoilAlerts={setActiveSoilAlerts}
                />
              ) : (
                <HumidityTemperatureAlertLimit
                  limits={limits}
                  measurements={measurements}
                  isFieldEditable={isFieldEditable}
                  isViewMode={isViewMode}
                  setLimits={setLimits}
                />
              )}
              {!isGraficoSuelo && !isViewMode && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={addLimit}
                  size="small"
                >
                  Agregar límite
                </Button>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mensaje"
                value={formData.message}
                onChange={handleTextChange('message')}
                multiline
                rows={3}
                required
                disabled={!isFieldEditable('message')}
              />
            </Grid>
            <Grid item xs={4}>
              <Box display="flex" alignItems="center" gap={1}>
                <FormControl fullWidth required>
                  <InputLabel>Frecuencia máxima de activación</InputLabel>
                  <Select
                    value={formData.maxFrequency}
                    onChange={handleSelectChange('maxFrequency')}
                    label="Frecuencia máxima de activación"
                    disabled={!isFieldEditable('maxFrequency')}
                  >
                    {MAX_FREQUENCY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Intervalo mínimo entre activaciones de la alerta para evitar spam">
                  <IconButton size="small">
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box display="flex" alignItems="center" gap={1}>
                <FormControl fullWidth required>
                  <InputLabel>Tiempo fuera de rango</InputLabel>
                  <Select
                    value={formData.timeOutRange}
                    onChange={handleSelectChange('timeOutRange')}
                    label="Tiempo fuera de rango"
                    disabled={!isFieldEditable('timeOutRange')}
                  >
                    {TIME_OUT_RANGE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Tiempo que debe permanecer la medición fuera del rango para activar la alerta">
                  <IconButton size="small">
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box display="flex" alignItems="center" gap={1}>
                <FormControl fullWidth required>
                  <InputLabel>Agenda de revisión</InputLabel>
                  <Select
                    value={formData.timeEachReview}
                    onChange={handleSelectChange('timeEachReview')}
                    label="Agenda de revisión"
                    disabled={!isFieldEditable('timeEachReview')}
                  >
                    {REVIEW_SCHEDULE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="Frecuencia con la que el sistema verificará si se cumplen las condiciones de la alerta">
                  <IconButton size="small">
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Destinatarios</Typography>
                <Tooltip title="Usuarios que recibirán las notificaciones cuando se active la alerta">
                  <IconButton size="small">
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox"></TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Número telefónico</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(users.concat(customUsers)).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">No hay usuarios</TableCell>
                      </TableRow>
                    ) : (
                      users.concat(customUsers).map((user) => (
                        <TableRow key={user.id}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedUsers.includes(user.email)}
                              onChange={() => handleToggleUser(user.email)}
                              color="primary"
                              disabled={!isFieldEditable('recipients')}
                            />
                          </TableCell>
                          <TableCell>{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>{user.phoneNumber || '-'}</TableCell>
                          <TableCell>
                            {!isViewMode && user.id.startsWith('custom-') && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setCustomUsers(customUsers.filter((u) => u.id !== user.id));
                                  setSelectedUsers(selectedUsers.filter((e) => e !== user.email));
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {!isViewMode && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowAddDest((prev) => !prev)}
                    size="small"
                    disabled={!isFieldEditable('recipients')}
                  >
                    Agregar destinatario
                  </Button>
                  {showAddDest && (
                    <>
                      <TextField
                        label="Nombre"
                        value={newDest.name}
                        onChange={(e) => setNewDest({ ...newDest, name: e.target.value })}
                        size="small"
                      />
                      <TextField
                        label="Correo"
                        value={newDest.email}
                        onChange={(e) => setNewDest({ ...newDest, email: e.target.value })}
                        size="small"
                      />
                      <TextField
                        label="Teléfono"
                        value={newDest.phone}
                        onChange={(e) => setNewDest({ ...newDest, phone: e.target.value })}
                        size="small"
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          if (
                            newDest.name
                            && newDest.email
                            && newDest.phone
                            && !customUsers.some((u) => u.email === newDest.email)
                            && !users.some((u) => u.email === newDest.email)
                          ) {
                            const customUser = {
                              id: `custom-${Date.now()}-${Math.random()}`,
                              name: newDest.name,
                              email: newDest.email,
                              phoneNumber: newDest.phone,
                            };
                            setCustomUsers([...customUsers, customUser]);
                            setSelectedUsers([...selectedUsers, newDest.email]);
                            setNewDest({ name: '', email: '', phone: '' });
                            setShowAddDest(false);
                          }
                        }}
                        disabled={!(newDest.name && newDest.email && newDest.phone)}
                      >
                        Agregar
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {isViewMode ? 'Cerrar' : 'Cancelar'}
        </Button>
        {!isViewMode && (
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={endDateInPast}
          >
            {mode === AlertModalMode.UPDATE ? 'Actualizar' : 'Guardar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AlertModal;
